import dayjs from 'dayjs'
import type { OKRes } from '../../Types/Api/Ok'
import type {
  PostDataType,
  RelatedConditionRes,
  SavePostReq,
} from '../../Types/Api/Post'
import { api } from './API'
import { isResErrorType, requireAuth } from './Auth'

//
// 投稿
//

export const findPost = async (postId: number, name: string) => {
  const res = await requireAuth<PostDataType>(
    api.get(`posts/${postId}`, { site_name: name }),
  )

  if (res.ok && res.data && !isResErrorType(res.data)) {
    const {
      title,
      body,
      slug,
      created_at,
      updated_at,
      cv,
      post_type_id,
      status_label,
      fields,
      ...rest
    } = res.data
    return { ...rest, fields: { ...fields, title, body, slug } }
  }

  return null
}

export const getMaxSlug = async (postTypeId: number, name: string) => {
  const res = await requireAuth<any>(
    api.get(`posts/max_slug`, { post_type_id: postTypeId, site_name: name }),
  )

  if (res.ok && res.data && !isResErrorType(res.data)) {
    return res.data.max_slug
  }

  return null
}

export const findPosts = async (
  postTypeId: number,
  page: number,
  name: string,
  search_conditions: {
    status?: string
    post_date?: string[]
    title?: string
    taxonomies?: {
      term_id: number[]
    }[]
  },
  sort_conditon: {
    order_by: 'created_at' | 'post_date' | 'updated_at'
    asc: number
  } = {
    order_by: 'created_at',
    asc: 0,
  },
) => {
  const { status, ...rest } = search_conditions
  const conditionParams = JSON.stringify(rest)
  const { order_by, asc } = sort_conditon

  const res = await requireAuth<any>(
    api.get(`posts`, {
      post_type_id: postTypeId,
      page,
      filter: status,
      order_by: order_by,
      asc: asc,
      site_name: name,
      conditionParams: conditionParams,
    }),
  )

  if (!res.ok) {
    return res
  }

  return {
    ...res,
    data: {
      ...res.data,
      data: res.data.data.map((post) => ({
        ...post,
        post_date: dayjs(post.post_date),
        created_at: dayjs(post.created_at),
        updated_at: dayjs(post.updated_at),
      })),
    },
  }
}

export const createPost = (values: FormData | SavePostReq, name: string) => {
  return requireAuth<PostDataType>(api.post(`posts?site_name=${name}`, values))
}

export const updatePost = (
  id: number,
  values: FormData | SavePostReq,
  name: string,
) => {
  return requireAuth<PostDataType>(
    api.post(`posts/${id}?site_name=${name}`, values),
  )
}

export const deletePost = (id: number, name: string) => {
  return requireAuth<OKRes>(api.delete(`posts/${id}`, { site_name: name }))
}

export const previewPost = (values: FormData | SavePostReq, name: string) => {
  return requireAuth<PostDataType & { preview_url: string }>(
    api.post(`preview?site_name=${name}`, values),
  )
}

export const duplicatePost = (id: number, name: string, postTypeId: number) => {
  return requireAuth<PostDataType>(
    api.post(`posts/duplicate/${id}`, {
      site_name: name,
      post_type_id: postTypeId,
    }),
  )
}

export const viewPost = (postId, name) => {
  return requireAuth<any>(api.get(`posts/view/${postId}`, { site_name: name }))
}

export const findDefaultRelatedConditions = async (postTypeId: number) => {
  return requireAuth<RelatedConditionRes[]>(
    api.get(`related_post_conditions/search`, {
      type: 'default',
      post_type_id: postTypeId,
    }),
  )
}

export const findCustomRelatedConditions = async (postId: number) => {
  return requireAuth<RelatedConditionRes[]>(
    api.get(`related_post_conditions/search`, {
      type: 'custom',
      post_id: postId,
    }),
  )
}

export const storeDefaultRelatedCondition = async (
  values: RelatedConditionRes[],
) => {
  return requireAuth<RelatedConditionRes[]>(
    api.post(`related_post_conditions/upsert`, {
      conditions: values,
    }),
  )
}
